<template>
    <div class="flex items-center gap-2" role="group" :aria-label="label">
        <span v-if="label" class="mr-2 text-sm font-semibold">{{ label }}</span>

        <a
            class="icon icon-sm h-9 w-9 rounded-full bg-primary-500 text-white hover:bg-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            :href="linkedInUrl"
            target="_blank"
            rel="noopener noreferrer"
            @click.prevent="openPopup(linkedInUrl)"
        >
            <slot name="linkedin-icon"></slot>
            <span class="sr-only">Share on LinkedIn</span>
        </a>

        <a
            class="icon icon-sm h-9 w-9 rounded-full bg-primary-500 text-white hover:bg-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            :href="facebookUrl"
            target="_blank"
            rel="noopener noreferrer"
            @click.prevent="openPopup(facebookUrl)"
        >
            <slot name="facebook-icon"></slot>
            <span class="sr-only">Share on Facebook</span>
        </a>

        <a
            class="icon icon-sm h-9 w-9 rounded-full bg-primary-500 text-white hover:bg-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            :href="mailtoUrl"
        >
            <slot name="email-icon"></slot>
            <span class="sr-only">Share via email</span>
        </a>

        <button
            type="button"
            class="icon icon-sm relative h-9 w-9 rounded-full bg-primary-500 text-white hover:bg-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            @click="copyLink"
        >
            <slot name="link-icon"></slot>
            <span class="sr-only">Copy link</span>
            <transition
                enter-class="opacity-0 translate-y-1"
                enter-active-class="transition-all duration-200"
                enter-to-class="opacity-100 translate-y-0"
                leave-class="opacity-100"
                leave-active-class="transition-all duration-200"
                leave-to-class="opacity-0"
            >
                <span
                    v-if="copied"
                    class="absolute left-1/2 top-full mt-2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white"
                    role="status"
                >
                    Link copied
                </span>
            </transition>
        </button>
    </div>
</template>

<script>
const POPUP_FEATURES = 'width=600,height=540,resizable=yes,scrollbars=yes';

export default {
    name: 'ShareToolbar',

    props: {
        url: {
            type: String,
            required: true,
        },
        title: {
            type: String,
            default: '',
        },
        description: {
            type: String,
            default: '',
        },
        label: {
            type: String,
            default: 'Share',
        },
    },

    data() {
        return {
            copied: false,
            copyTimer: null,
        };
    },

    computed: {
        encodedUrl() {
            return encodeURIComponent(this.url);
        },

        encodedTitle() {
            return encodeURIComponent(this.title);
        },

        encodedDescription() {
            return encodeURIComponent(this.description);
        },

        linkedInUrl() {
            return `https://www.linkedin.com/sharing/share-offsite/?url=${this.encodedUrl}`;
        },

        facebookUrl() {
            return `https://www.facebook.com/sharer/sharer.php?u=${this.encodedUrl}`;
        },

        mailtoUrl() {
            const subject = this.encodedTitle;
            const body = encodeURIComponent(`${this.title}\n\n${this.url}\n\n${this.description}`.trim());

            return `mailto:?subject=${subject}&body=${body}`;
        },
    },

    beforeDestroy() {
        if (this.copyTimer) {
            clearTimeout(this.copyTimer);
        }
    },

    methods: {
        openPopup(targetUrl) {
            window.open(targetUrl, 'share-popup', POPUP_FEATURES);
            this.$emit('share', targetUrl);
        },

        async copyLink() {
            try {
                if (navigator.clipboard && window.isSecureContext) {
                    await navigator.clipboard.writeText(this.url);
                } else {
                    this.fallbackCopy(this.url);
                }
                this.showCopied();
                this.$emit('copy', this.url);
            } catch (error) {
                this.fallbackCopy(this.url);
                this.showCopied();
            }
        },

        fallbackCopy(text) {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'absolute';
            textarea.style.left = '-9999px';
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
        },

        showCopied() {
            this.copied = true;
            if (this.copyTimer) {
                clearTimeout(this.copyTimer);
            }
            this.copyTimer = setTimeout(() => {
                this.copied = false;
            }, 2000);
        },
    },
};
</script>
